import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Brand assets
const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function MiningTable3() {
  const pdfRef = useRef();

  const data = [
    { year: 2020, all_sexes: 7409, female: 910, male: 6499 },
    { year: 2021, all_sexes: 7909, female: 958, male: 6951 },
    { year: 2022, all_sexes: 8623, female: 1104, male: 7519 },
    { year: 2023, all_sexes: 9037, female: 1238, male: 7799 },
    { year: 2024, all_sexes: 9426, female: 1324, male: 8102 },
    { year: null, all_sexes: 8481, female: 1107, male: 7374 },
  ].filter((d) => d.year !== null);

  const avgFemale =
    data.reduce((sum, row) => sum + row.female, 0) / data.length;
  const avgMale = data.reduce((sum, row) => sum + row.male, 0) / data.length;
  const avgAll =
    data.reduce((sum, row) => sum + row.all_sexes, 0) / data.length;

  // Chart Data
  const chartData = {
    labels: data.map((d) => d.year),
    datasets: [
      {
        label: "Female Employment",
        data: data.map((d) => d.female),
        backgroundColor: "rgba(236, 72, 153, 0.85)", // pink
        borderRadius: 6,
      },
      {
        label: "Male Employment",
        data: data.map((d) => d.male),
        backgroundColor: "rgba(14, 165, 233, 0.85)", // blue
        borderRadius: 6,
      },
      {
        label: "Female Average",
        data: Array(data.length).fill(avgFemale),
        borderColor: "rgba(236, 72, 153, 0.9)",
        borderDash: [6, 6],
        borderWidth: 2,
        fill: false,
        type: "line",
        tension: 0.3,
      },
      {
        label: "Male Average",
        data: Array(data.length).fill(avgMale),
        borderColor: "rgba(14, 165, 233, 0.9)",
        borderDash: [6, 6],
        borderWidth: 2,
        fill: false,
        type: "line",
        tension: 0.3,
      },
    ],
  };

  // Chart Options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Utah Mining Employment by Gender (2020–2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            if (context.dataset.label.includes("Average")) {
              return `${context.dataset.label}: ${context.parsed.y.toFixed(1)}`;
            }
            return `${context.dataset.label}: ${context.parsed.y.toLocaleString("en-US")}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Year" },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Number of Employees" },
      },
    },
  };

  // Summary Insights
  const startYear = data[0];
  const endYear = data[data.length - 1];
  const growth = (
    ((endYear.all_sexes - startYear.all_sexes) / startYear.all_sexes) *
    100
  ).toFixed(2);
  const peakYear = data.reduce((max, d) =>
    d.all_sexes > max.all_sexes ? d : max
  );
  const femaleShare2024 = ((endYear.female / endYear.all_sexes) * 100).toFixed(
    1
  );

  // PDF Export
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2, // higher for sharper text
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      // A4 size in mm
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Convert canvas height to mm
      const imgWidth = pageWidth - 10; // leave small margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      // Header
      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      // Footer
      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // More pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Footer for the last page
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("utah_mining_gender_employment.pdf");
      // input.style.width = originalWidth;
    });
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-pink-50 via-white to-blue-50 rounded-xl shadow-md"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Utah Mining Employment by Gender (2020–2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Overall Growth (2020–2024)
            </p>
            <p className="text-xl font-bold text-blue-800">+{growth}%</p>
          </div>
          <div className="p-4 bg-pink-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Female Share in 2024
            </p>
            <p className="text-xl font-bold text-pink-800">
              {femaleShare2024}%
            </p>
          </div>
          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Peak Year</p>
            <p className="text-xl font-bold text-yellow-800">{peakYear.year}</p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          {/* <div className="flex flex-col h-full"> */}
          <div className="overflow-x-auto flex items-center">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Year</th>
                  <th className="p-2">Female</th>
                  <th className="p-2">Male</th>
                  <th className="p-2">All Sexes</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr
                    key={row.year}
                    className={`${
                      row.year === 2024
                        ? "bg-green-100 font-bold"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <td className="p-2">{row.year}</td>
                    <td className="p-2 text-pink-700">{row.female.toLocaleString()}</td>
                    <td className="p-2 text-blue-700">{row.male.toLocaleString()}</td>
                    <td className="p-2 font-semibold text-gray-900">
                      {row.all_sexes.toLocaleString()}
                    </td>
                  </tr>
                ))}

                {/* Average Row */}
                <tr className="bg-pink-100 font-bold text-gray-900">
                  <td className="p-2">Average</td>
                  <td className="p-2 text-pink-700">{avgFemale.toLocaleString(undefined, {maximumFractionDigits: 1})}</td>
                  <td className="p-2 text-blue-700">{avgMale.toLocaleString(undefined, {maximumFractionDigits: 1})}</td>
                  <td className="p-2 font-semibold text-gray-900">
                    {avgAll.toLocaleString(undefined, {maximumFractionDigits: 1})}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {/* </div> */}

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-center text-gray-700 mt-4">
          Utah’s mining sector shows a steady increase in both male and female
          employment, with female participation rising from 910 in 2020 to 1,324
          in 2024.
        </p>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
